import { useEffect, useState } from "react";
import { api } from "../lib/api";
import AdminShell from "../components/AdminShell";
import { CheckCircle2, Loader2, CalendarDays } from "lucide-react";

const FIELDS = [
  { key: "marketing_budget", label: "Marketing Budget", hint: "Total spend for the month (USD)" },
  { key: "website_visits", label: "Website Visits", hint: "Total visits this month" },
  { key: "cost_per_click", label: "Cost per Click", hint: "Average CPC (USD)" },
  { key: "success_fee_avg_earnings", label: "Success Fee Average Earnings", hint: "Average earnings per success fee (USD)" },
];

const monthLabel = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-GB",
    { month: "long", year: "numeric", timeZone: "UTC" });
};

export default function AdminMonthlyReport() {
  const currentMonth = new Date().toISOString().slice(0, 7);
  const [month, setMonth] = useState(currentMonth);
  const [form, setForm] = useState({});
  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    setLoading(true); setSaved(false); setErr("");
    api.get(`/admin/monthly-report?month=${month}`).then(({ data }) => {
      setExisting(data.data);
      const f = {};
      FIELDS.forEach(({ key }) => { f[key] = data.data?.[key] ?? ""; });
      setForm(f);
    }).catch(() => setErr("Couldn't load this month's data"))
      .finally(() => setLoading(false));
  }, [month]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setSaved(false); setErr("");
    try {
      const { data } = await api.put("/admin/monthly-report", { month, ...form });
      setExisting(data.data);
      setSaved(true);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <AdminShell current="/admin/monthly-report">
      <div className="mx-auto max-w-2xl">
        <h1 className="text-3xl font-bold">Monthly Report</h1>
        <p className="mt-1 text-sm text-neutral-400">
          Manual figures used in the monthly performance report.
        </p>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#F59E0B]/30 bg-[#F59E0B]/10 px-4 py-3">
          <div className="flex items-center gap-2 text-sm font-bold text-[#F59E0B]"
               data-testid="monthly-report-month">
            <CalendarDays className="h-4 w-4" />
            You are filling in: {monthLabel(month)}
          </div>
          <input type="month" value={month} max={currentMonth}
            onChange={(e) => e.target.value && setMonth(e.target.value)}
            data-testid="monthly-report-month-picker"
            className="rounded-lg border border-neutral-700 bg-neutral-900 px-2 py-1 text-xs text-neutral-200 outline-none focus:border-[#F59E0B]" />
        </div>

        {existing && (
          <p className="mt-3 text-xs text-neutral-400" data-testid="monthly-report-prefilled-note">
            Data for this month was saved on{" "}
            {new Date(existing.updated_at).toLocaleDateString("en-GB",
              { day: "numeric", month: "short", year: "numeric" })}{" "}
            — fields are pre-filled, edit and resubmit to update.
          </p>
        )}

        {loading ? (
          <div className="mt-10 flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-[#F59E0B]" />
          </div>
        ) : (
          <form onSubmit={submit}            className="mt-6 space-y-5 rounded-2xl border border-neutral-800 bg-neutral-900 p-6">
            {FIELDS.map(({ key, label, hint }) => (
              <div key={key}>
                <label className="block text-sm font-bold text-neutral-200">{label}</label>
                <p className="text-xs text-neutral-500">{hint}</p>
                <input type="number" step="any" min="0" required
                  value={form[key] ?? ""}
                  onChange={(e) => { setForm({ ...form, [key]: e.target.value }); setSaved(false); }}
                  data-testid={`monthly-report-${key.replace(/_/g, "-")}`}
                  className="mt-2 w-full rounded-lg border border-neutral-700 bg-neutral-950 px-3 py-2.5 text-sm text-white outline-none focus:border-[#F59E0B]" />
              </div>
            ))}
            {err && <p className="text-sm text-red-400" data-testid="monthly-report-error">{String(err)}</p>}
            {saved && (
              <p className="flex items-center gap-2 rounded-lg bg-green-500/10 px-3 py-2 text-sm font-semibold text-green-400"
                 data-testid="monthly-report-saved">
                <CheckCircle2 className="h-4 w-4" />
                Saved — figures for {monthLabel(month)} are recorded.
              </p>
            )}
            <button type="submit" disabled={saving}
              data-testid="monthly-report-submit"
              className="w-full rounded-lg bg-[#F59E0B] py-2.5 text-sm font-bold text-neutral-950 hover:bg-[#EAB308] disabled:opacity-50">
              {saving ? "Saving…" : existing ? "Update figures" : "Save figures"}
            </button>
          </form>
        )}

        <ReportTools month={month} />
      </div>
    </AdminShell>
  );
}

function ReportTools({ month }) {
  const [busy, setBusy] = useState("");
  const [html, setHtml] = useState("");
  const [msg, setMsg] = useState("");
  const [history, setHistory] = useState([]);
  const [schedule, setSchedule] = useState(null);

  const loadHistory = () => {
    api.get("/admin/monthly-report/history").then((r) => setHistory(r.data || [])).catch(() => {});
    api.get("/admin/monthly-report/schedule-log").then((r) => setSchedule(r.data)).catch(() => {});
  };
  useEffect(() => { loadHistory(); }, []);

  const preview = async () => {
    setBusy("preview"); setMsg("");
    try {
      const { data } = await api.post(`/admin/monthly-report/compile?month=${month}`);
      setHtml(data.html);
      setMsg(`Report compiled for ${data.month} and stored.`);
      loadHistory();
    } catch (e) { setMsg(e?.response?.data?.detail || "Compile failed"); }
    finally { setBusy(""); }
  };

  const sendTest = async () => {
    const email = window.prompt("Send the report to which email?", "capital@clinq.africa");
    if (!email) return;
    setBusy("test"); setMsg("");
    try {
      const { data } = await api.post("/admin/monthly-report/send-test",
        { month, email, include_pdf: true });
      setMsg(`Report sent to ${data.to} with the 4-page PDF attached.`);
      loadHistory();
    } catch (e) { setMsg(e?.response?.data?.detail || "Send failed"); }
    finally { setBusy(""); }
  };

  const viewPast = async (id) => {
    setMsg("");
    try {
      const { data } = await api.get(`/admin/monthly-report/history/${id}`);
      setHtml(data.html);
    } catch { setMsg("Couldn't load that report"); }
  };

  return (
    <div className="mt-10">
      <h2 className="text-xl font-bold">Performance Report</h2>
      <p className="mt-1 text-sm text-neutral-400">
        Compile the emailed report (current month + 3 preceding, rolling).
      </p>
      <div className="mt-4 flex flex-wrap gap-2">
        <button onClick={preview} disabled={!!busy}
          data-testid="monthly-report-preview-btn"
          className="rounded-lg bg-[#F59E0B] px-4 py-2 text-sm font-bold text-neutral-950 hover:bg-[#EAB308] disabled:opacity-50">
          {busy === "preview" ? "Compiling…" : "Generate & preview report"}
        </button>
        <button onClick={sendTest} disabled={!!busy}
          data-testid="monthly-report-send-test-btn"
          className="rounded-lg border border-neutral-700 bg-neutral-800 px-4 py-2 text-sm font-semibold text-neutral-200 hover:text-white disabled:opacity-50">
          {busy === "test" ? "Sending…" : "Send report by email…"}
        </button>
      </div>
      {msg && <p className="mt-3 text-sm font-semibold text-[#F59E0B]" data-testid="monthly-report-tools-msg">{msg}</p>}

      {html && (
        <div className="mt-5 overflow-hidden rounded-2xl border border-neutral-700 bg-white"
             data-testid="monthly-report-preview">
          <iframe title="Report preview" srcDoc={html}
            className="h-[720px] w-full" sandbox="" />
        </div>
      )}

      {history.length > 0 && (
        <div className="mt-8">
          <h3 className="text-sm font-bold uppercase tracking-wider text-neutral-400">Past reports</h3>          <div className="mt-2 divide-y divide-neutral-800 rounded-xl border border-neutral-800 bg-neutral-900"
               data-testid="monthly-report-history">
            {history.map((r) => (
              <button key={r.report_id} onClick={() => viewPast(r.report_id)}
                data-testid={`monthly-report-history-${r.report_id}`}
                className="flex w-full items-center justify-between px-4 py-2.5 text-left text-sm text-neutral-300 hover:bg-neutral-800">
                <span className="font-semibold text-white">{r.month}</span>
                <span className="text-xs text-neutral-500">
                  compiled {new Date(r.generated_at).toLocaleString("en-GB",
                    { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8">
        <h3 className="text-sm font-bold uppercase tracking-wider text-neutral-400">Scheduled sends</h3>
        <p className="mt-1 text-xs text-neutral-500">
          Report: last day of month, 11pm EAT · Reminders: 25th &amp; 28th, 9am EAT
          {schedule && <> · to <b className="text-neutral-300">{schedule.recipient}</b>
            {!schedule.enabled && <span className="ml-2 text-red-400">(scheduler disabled)</span>}</>}
        </p>
        {schedule?.rows?.length ? (
          <div className="mt-2 overflow-hidden rounded-xl border border-neutral-800 bg-neutral-900"
               data-testid="monthly-report-schedule-log">
            <table className="w-full text-xs">
              <thead className="bg-neutral-950 text-left uppercase tracking-wider text-neutral-500">
                <tr>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">Month</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Sent at</th>
                </tr>
              </thead>
              <tbody>
                {schedule.rows.map((r, i) => (
                  <tr key={i} className="border-t border-neutral-800 text-neutral-300">
                    <td className="px-3 py-2 font-semibold text-white">
                      {r.kind === "report" ? "Performance report"
                        : r.kind === "reminder_25" ? "Reminder (25th)" : "Final reminder (28th)"}
                    </td>
                    <td className="px-3 py-2">{r.month}</td>
                    <td className="px-3 py-2">
                      <span className={
                        r.status === "sent" ? "font-bold text-green-400"
                        : r.status === "failed" ? "font-bold text-red-400"
                        : "text-neutral-400"}>
                        {r.status.replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      {r.sent_at ? new Date(r.sent_at).toLocaleString("en-GB",
                        { day: "numeric", month: "short", year: "numeric",
                          hour: "2-digit", minute: "2-digit" }) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="mt-2 text-xs text-neutral-500" data-testid="monthly-report-schedule-empty">
            Nothing sent yet — the first scheduled email will appear here.
          </p>
        )}
      </div>
    </div>
  );
}
